import React from 'react';
import { Platform, View, Text, TouchableOpacity, ScrollView, Switch, useWindowDimensions } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import LHOverlay from '../../../../components/LHOverlay';
import ConfirmDiscardModal from '../../../../components/ConfirmDiscardModal';
import LhTextInput from '../../../../components/LhTextInput';
import LHDropDown from '../../../../components/LHDropDown';
import LhButton from '../../../../components/LhButton';
import { useTheme } from '../../../../theme/useTheme';
import {
  LOCATION_TYPE_OPTIONS,
  ORGANIZATION_TYPE_OPTIONS,
  THREE_PL_OPTIONS,
  SPARC_CATEGORY_OPTIONS,
  FREQUENCY_OPTIONS,
  SPARC_SECTION_OPTIONS,
} from './kfaDropdownOptions';
import { styles } from './KfaAddOverlay.styles';
import KfaMultiSelectDropdown from './KfaMultiSelectDropdown';
import { useKfaForm, MODULE_DISPLAY_NAMES } from './useKfaForm';

// KfaInitialData kept for backward compatibility (used by legacy callers)
export interface KfaInitialData {
  keyFocusArea?: string;
  question?: string;
  category?: string;
  modules?: string;
}

// Full dataset for edit mode — mirrors the API payload fields
export interface KfaEditItem {
  id: string;
  negativeBehavior: string;
  question: string;
  sparcCategory?: string;
  locationType?: string;
  organizationType?: string;
  is3pl?: boolean;
  frequency?: string;
  zoneIds?: number[];
  modules?: string;
  sparcSection?: string;
  status?: boolean;
  details?: string;
  detailLink?: string;
  topicType?: string;
  additionalModules?: string[];
  /** ISO timestamp — used to enforce the 24-hour edit lock on text fields */
  createdAt?: string;
}

interface KfaAddOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  mode?: 'create' | 'edit';
  selectedKfa?: KfaEditItem;
  selectedModule?: string;
  onSaveSuccess?: (moduleNames?: string[]) => void;
}

const KfaAddOverlay: React.FC<KfaAddOverlayProps> = ({
  visible, onDismiss, mode = 'create', selectedKfa, selectedModule, onSaveSuccess,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { width: screenWidth } = useWindowDimensions();
  const isMobile = screenWidth < 768;

  const {
    keyFocusArea, setKeyFocusArea,
    question, setQuestion,
    locationType, setLocationType,
    organizationType, setOrganizationType,
    threeplStatus, setThreeplStatus,
    category, setCategory,
    modules, setModules,
    observationType, setObservationType,
    section, setSection,
    roles, setRoles,
    leadershipZone, setLeadershipZone,
    leadershipFrequency, setLeadershipFrequency,
    statusEnabled, setStatusEnabled,
    sparcZone, setSparcZone,
    sparcSection, setSparcSection,
    availableZones,
    isFetchingZones,
    nhoTouchType, setNhoTouchType,
    nhoSection, setNhoSection,
    rackingSection, setRackingSection,
    siteEvaluationSection, setSiteEvaluationSection,
    isTextLocked,
    validationMessage,
    fieldErrors,
    isDirtyRef,
    showDiscardConfirm, setShowDiscardConfirm,
    pendingUncheckModule, setPendingUncheckModule,
    isSaving,
    saveError,
    handleSave,
    handleCancel,
    clearFieldError,
    syncModuleSelectionError,
    handleModuleUncheck,
    clearModuleFields,
  } = useKfaForm({ visible, mode, selectedKfa, selectedModule, onDismiss, onSaveSuccess });

  // Display names for the multi-module info banner
  const MODULE_KEY_TO_NAME: Record<string, string> = {
    shiftHandover: 'Shift Handover',
    leadershipWalk: 'Leadership Walk',
    sparcWalk: 'SPARC Walk',
    observations: 'Observations',
    nho: 'NHO',
    rackingInspection: 'Racking Inspection',
    siteEvaluation: 'Site Evaluation',
  };

  return (
    <>
      <ConfirmDiscardModal
        visible={showDiscardConfirm}
        onKeepEditing={() => setShowDiscardConfirm(false)}
        onDiscard={() => { setShowDiscardConfirm(false); onDismiss(); }}
      />
      <ConfirmDiscardModal
        visible={pendingUncheckModule !== null}
        title="Remove Module?"
        message={`Removing ${MODULE_DISPLAY_NAMES[pendingUncheckModule ?? ''] ?? ''} will clear its configuration fields. Continue?`}
        keepLabel="Cancel"
        discardLabel="Continue"
        onKeepEditing={() => setPendingUncheckModule(null)}
        onDiscard={() => {
          if (pendingUncheckModule) {
            clearModuleFields(pendingUncheckModule);
            const nextModules = { ...modules, [pendingUncheckModule]: false };
            setModules(nextModules);
            syncModuleSelectionError(nextModules);
          }
          setPendingUncheckModule(null);
        }}
      />
      <LHOverlay
        visible={visible}
        onDismiss={handleCancel}
        title={mode === 'edit' ? 'Edit Key Focus Area' : 'Add Key Focus Area'}
        size="medium"
        position="center"
        close={true}
      >
        <View style={styles.overlayWrapper}>
          {validationMessage ? (
            <View style={[styles.saveErrorBox, isDark && { backgroundColor: colors.statusBackground.error, borderLeftColor: colors.status.error }]}>
              <MaterialIcons name="error-outline" size={16} color={isDark ? colors.status.error : '#C62828'} style={{ marginRight: 8 }} />
              <Text style={[styles.saveErrorText, isDark && { color: colors.status.error }]}>{validationMessage}</Text>
            </View>
          ) : null}
          {saveError && visible ? (
            <View style={[styles.saveErrorBox, isDark && { backgroundColor: colors.statusBackground.error, borderLeftColor: colors.status.error }]}>
              <MaterialIcons name="error-outline" size={16} color={isDark ? colors.status.error : '#C62828'} style={{ marginRight: 8 }} />
              <Text style={[styles.saveErrorText, isDark && { color: colors.status.error }]}>{saveError}</Text>
            </View>
          ) : null}

          <ScrollView style={styles.scrollContent} contentContainerStyle={styles.overlayContent} showsVerticalScrollIndicator={true}>
            {/* Status Toggle — shown in edit mode */}
            {mode === 'edit' && (
              <View
                style={[
                  styles.statusRow,
                  isDark && {
                    backgroundColor: colors.background.surface,
                    borderColor: colors.border.default,
                  },
                ]}
              >
                <Text style={[styles.statusLabel, isDark && { color: colors.text.primary }]}>Status:</Text>
                <Switch
                  value={statusEnabled}
                  onValueChange={(val) => {
                    isDirtyRef.current = true;
                    setStatusEnabled(val);
                  }}
                  thumbColor={'#FFFFFF'}
                  trackColor={{ false: isDark ? '#555555' : '#CCCCCC', true: '#2E7D32' }}
                />
                <Text style={[styles.statusValue, { color: statusEnabled ? '#2E7D32' : (isDark ? colors.text.secondary : '#757575') }]}>
                  {statusEnabled ? 'Enabled' : 'Disabled'}
                </Text>
              </View>
            )}

            {/* Multi-module info message */}
            {Object.values(modules).filter(Boolean).length > 1 && (
              <View style={[styles.infoBox, isDark && { backgroundColor: colors.statusBackground.info, borderLeftColor: colors.brand.primary }]}>
                <MaterialIcons name="info" size={20} color={isDark ? colors.brand.primary : '#034895'} style={styles.infoIcon} />
                <Text style={[styles.infoText, isDark && { color: colors.brand.primary }]}>
                  {`Text is shared across ${(Object.entries(modules) as [string, boolean][]).filter(([, v]) => v).map(([k]) => MODULE_KEY_TO_NAME[k]).filter(Boolean).join(', ')}. Editing updates all modules.`}
                </Text>
              </View>
            )}

            {/* Assessment Text Section */}
            <View style={styles.assessmentSection}>
              <View style={[styles.sectionTitleContainer, isDark && { borderBottomColor: colors.border.divider }]}>
                <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>Assessment Text</Text>
              </View>

              {isTextLocked && (
                <View style={[styles.infoBox, { marginBottom: 12 }, isDark && { backgroundColor: colors.statusBackground.info, borderLeftColor: colors.brand.primary }]}>
                  <MaterialIcons name="lock" size={20} color={isDark ? colors.brand.primary : '#034895'} style={styles.infoIcon} />
                  <Text style={[styles.infoText, isDark && { color: colors.brand.primary }]}>Text fields are locked 24 hours after creation.</Text>
                </View>
              )}

              <View style={styles.inputGroup}>
                <View style={styles.labelRow}>
                  <Text style={[styles.inputLabel, isDark && { color: colors.text.primary }]}>
                    Key Focus Area <Text style={[styles.greyText, isDark && { color: colors.text.secondary }]}>(negative observation statement)</Text>
                  </Text>
                  {modules.shiftHandover && !modules.leadershipWalk && !modules.sparcWalk
                    ? <Text style={[styles.greyText, isDark && { color: colors.text.secondary }]}>*one of KFA / Question required</Text>
                    : <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>}
                </View>
                <LhTextInput
                  value={keyFocusArea}
                  onChangeText={(val) => {
                    setKeyFocusArea(val);
                    clearFieldError('keyFocusArea');
                    if (modules.shiftHandover) clearFieldError('question');
                  }}
                  placeholder="e.g., Employee did not honk at the intersection"
                  multiline
                  numberOfLines={3}
                  editable={!isTextLocked}
                  style={[
                    styles.textInput,
                    isDark && {
                      color: colors.text.primary,
                      backgroundColor: colors.input.background,
                    },
                  ]}
                  wrapperStyle={[
                    styles.inputWrapper,
                    isDark && { borderColor: colors.input.border },
                    fieldErrors.keyFocusArea && (isDark ? { borderColor: colors.status.error, backgroundColor: colors.statusBackground.error } : styles.inputError),
                    isTextLocked && (isDark ? { backgroundColor: colors.border.default } : styles.inputLocked),
                  ]}
                />
                <View style={styles.charCountRow}>
                  {isTextLocked && <Text style={[styles.lockedHelperText, isDark && { color: colors.text.secondary }]}>Locked 24 hours after creation.</Text>}
                  <Text style={[styles.charCount, isDark && { color: colors.text.secondary }]}>{keyFocusArea.length}/500</Text>
                </View>
              </View>

              <View style={styles.inputGroup}>
                <View style={styles.labelRow}>
                  <Text style={[styles.inputLabel, isDark && { color: colors.text.primary }]}>
                    Question{(modules.shiftHandover && !modules.leadershipWalk && !modules.sparcWalk) ? '' : ' '}
                    {(modules.shiftHandover && !modules.leadershipWalk && !modules.sparcWalk)
                      ? null
                      : <Text style={[styles.greyText, isDark && { color: colors.text.secondary }]}>(optional override for question-based modules)</Text>}
                  </Text>
                  {(modules.shiftHandover && !modules.leadershipWalk && !modules.sparcWalk)
                    ? <Text style={[styles.greyText, isDark && { color: colors.text.secondary }]}>*one of KFA / Question required</Text>
                    : <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>}
                </View>
                <LhTextInput
                  value={question}
                  onChangeText={(val) => {
                    isDirtyRef.current = true;
                    setQuestion(val);
                    if (modules.shiftHandover) {
                      clearFieldError('question');
                      clearFieldError('keyFocusArea');
                    } else {
                      clearFieldError('question');
                    }
                  }}
                  placeholder="e.g., Employee did not honk at the intersection?"
                  multiline
                  numberOfLines={3}
                  editable={!isTextLocked}
                  style={[
                    styles.textInput,
                    isDark && {
                      color: colors.text.primary,
                      backgroundColor: colors.input.background,
                    },
                  ]}
                  wrapperStyle={[
                    styles.inputWrapper,
                    isDark && { borderColor: colors.input.border },
                    fieldErrors.question && (isDark ? { borderColor: colors.status.error, backgroundColor: colors.statusBackground.error } : styles.inputError),
                    isTextLocked && (isDark ? { backgroundColor: colors.border.default } : styles.inputLocked),
                  ]}
                />
                <View style={styles.questionFooter}>
                  {!(modules.shiftHandover && !modules.leadershipWalk && !modules.sparcWalk) && <Text style={[styles.usageText, isDark && { color: colors.text.secondary }]}>Used by Pulse Check, NHO, Site Evaluation.</Text>}
                  {isTextLocked && <Text style={[styles.lockedHelperText, isDark && { color: colors.text.secondary }]}>Locked 24 hours after creation.</Text>}
                  <Text style={[styles.charCount, isDark && { color: colors.text.secondary }]}>{question.length}/500</Text>
                </View>
              </View>

              <View style={[styles.infoBox, isDark && { backgroundColor: colors.statusBackground.info, borderLeftColor: colors.brand.primary }]}>
                <MaterialIcons name="info" size={20} color={isDark ? colors.brand.primary : '#034895'} style={styles.infoIcon} />
                <Text style={[styles.infoText, isDark && { color: colors.brand.primary }]}>
                  The Key Focus Area text is the default for all modules. If a Question is provided, modules/sections that use question format will display it instead.
                </Text>
              </View>
            </View>

            {/* Shared Attributes Section */}
            <View style={styles.sharedAttributesSection}>
              <View style={[styles.sectionTitleContainer, isDark && { borderBottomColor: colors.border.divider }]}>
                <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>Shared Attributes</Text>
              </View>

              <View style={[isMobile ? styles.dropdownRowSingle : styles.dropdownRow, { zIndex: 20 }]}>
                <View style={[isMobile ? styles.dropdownColumnSingle : styles.dropdownColumn, { zIndex: 20 }]}>
                  <View style={fieldErrors.locationType && (isDark ? { borderColor: colors.status.error, borderWidth: 1, borderRadius: 6, padding: 4, backgroundColor: colors.statusBackground.error } : styles.dropdownErrorWrapper)}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Location Type</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <KfaMultiSelectDropdown
                      label=""
                      options={LOCATION_TYPE_OPTIONS}
                      selected={locationType}
                      onChange={(val) => { setLocationType(val); clearFieldError('locationType'); }}
                      placeholder="Select Location Type"
                    />
                  </View>
                </View>
                <View style={[isMobile ? styles.dropdownColumnSingle : styles.dropdownColumn, { zIndex: 20 }]}>
                  <View style={fieldErrors.organizationType && (isDark ? { borderColor: colors.status.error, borderWidth: 1, borderRadius: 6, padding: 4, backgroundColor: colors.statusBackground.error } : styles.dropdownErrorWrapper)}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Organization Type</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <KfaMultiSelectDropdown
                      label=""
                      options={ORGANIZATION_TYPE_OPTIONS}
                      selected={organizationType}
                      onChange={(val) => { setOrganizationType(val); clearFieldError('organizationType'); }}
                      placeholder="Select Organization Type"
                    />
                  </View>
                </View>
              </View>

              <View style={[isMobile ? styles.dropdownRowSingle : styles.dropdownRow, { zIndex: 10 }]}>
                <View style={[isMobile ? styles.dropdownColumnSingle : styles.dropdownColumn, { zIndex: 10 }]}>
                  <View style={fieldErrors.threeplStatus && (isDark ? { borderColor: colors.status.error, borderWidth: 1, borderRadius: 6, padding: 4, backgroundColor: colors.statusBackground.error } : styles.dropdownErrorWrapper)}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>3PL Status</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <KfaMultiSelectDropdown
                      label=""
                      options={THREE_PL_OPTIONS}
                      selected={threeplStatus ? [threeplStatus] : []}
                      onChange={(val) => {
                        setThreeplStatus(val.length > 0 ? val[0] : null);
                        clearFieldError('threeplStatus');
                      }}
                      placeholder="Select 3PL Status"
                      singleSelect
                    />
                  </View>
                </View>
                <View style={[isMobile ? styles.dropdownColumnSingle : styles.dropdownColumn, { zIndex: 10 }]}>
                  <View style={fieldErrors.category && (isDark ? { borderColor: colors.status.error, borderWidth: 1, borderRadius: 6, padding: 4, backgroundColor: colors.statusBackground.error } : styles.dropdownErrorWrapper)}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Category</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <KfaMultiSelectDropdown
                      label=""
                      options={SPARC_CATEGORY_OPTIONS}
                      selected={category}
                      onChange={(val) => { setCategory(val); clearFieldError('category'); }}
                      placeholder="Select Category"
                      singleSelect={!modules.sparcWalk}
                    />
                  </View>
                </View>
              </View>
            </View>

            {/* Module Assignment Section — hidden for Shift Handover (single module, auto-assigned) */}
            {selectedModule !== 'Shift Handover' && (
            <View style={[styles.moduleAssignmentSection, fieldErrors.moduleSelection && (isDark ? { borderColor: colors.status.error, borderWidth: 1, borderRadius: 8, padding: 8, backgroundColor: colors.statusBackground.error } : styles.sectionError)]}>
              <View style={[styles.sectionTitleContainer, isDark && { borderBottomColor: colors.border.divider }]}>
                <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>Module Assignment</Text>
              </View>

              {(!selectedModule) && (
                <View style={styles.checkboxGrid}>
                  <TouchableOpacity
                    style={[
                      styles.checkboxItem,
                      isDark && {
                        backgroundColor: colors.background.surface,
                        borderColor: colors.border.default,
                      },
                    ]}
                    onPress={() => {
                      const nextModules = { ...modules, shiftHandover: !modules.shiftHandover };
                      setModules(nextModules);
                      syncModuleSelectionError(nextModules);
                    }}
                  >
                    <MaterialIcons name={modules.shiftHandover ? 'check-box' : 'check-box-outline-blank'} size={20} color={isDark ? colors.brand.primary : '#034895'} style={styles.checkbox} />
                    <Text style={[styles.checkboxLabel, isDark && { color: colors.text.primary }]}>Shift Handover</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[
                      styles.checkboxItem,
                      isDark && {
                        backgroundColor: colors.background.surface,
                        borderColor: colors.border.default,
                      },
                    ]}
                    onPress={() => {
                      if (modules.leadershipWalk) {
                        handleModuleUncheck('leadershipWalk');
                      } else {
                        setModules({ ...modules, leadershipWalk: true });
                        clearFieldError('moduleSelection');
                      }
                    }}
                  >
                    <MaterialIcons name={modules.leadershipWalk ? 'check-box' : 'check-box-outline-blank'} size={20} color={isDark ? colors.brand.primary : '#034895'} style={styles.checkbox} />
                    <Text style={[styles.checkboxLabel, isDark && { color: colors.text.primary }]}>Leadership Walk</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[
                      styles.checkboxItem,
                      isDark && {
                        backgroundColor: colors.background.surface,
                        borderColor: colors.border.default,
                      },
                    ]}
                    onPress={() => {
                      if (modules.sparcWalk) {
                        handleModuleUncheck('sparcWalk');
                      } else {
                        setModules({ ...modules, sparcWalk: true });
                        clearFieldError('moduleSelection');
                      }
                    }}
                  >
                    <MaterialIcons name={modules.sparcWalk ? 'check-box' : 'check-box-outline-blank'} size={20} color={isDark ? colors.brand.primary : '#034895'} style={styles.checkbox} />
                    <Text style={[styles.checkboxLabel, isDark && { color: colors.text.primary }]}>SPARC Walk</Text>
                  </TouchableOpacity>
                </View>
              )}

              {fieldErrors.moduleSelection && (
                <View style={styles.moduleErrorRow}>
                  <MaterialIcons name="error-outline" size={14} color={isDark ? colors.status.error : '#C62828'} style={{ marginRight: 6 }} />
                  <Text style={[styles.moduleErrorText, isDark && { color: colors.status.error }]}>At least one module is required.</Text>
                </View>
              )}
            </View>
            )}

            {/* Observations Attributes */}
            {modules.observations && (
              <View style={[styles.attributesSection, isDark && { backgroundColor: colors.background.surface, borderLeftColor: colors.brand.primary }]}>
                <Text style={[styles.attributesSectionTitle, isDark && { color: colors.brand.primary }]}>Observations Attributes</Text>
                <View style={isMobile ? styles.attributeRowSingle : styles.attributeRow}>
                  <View style={isMobile ? styles.attributeColumnSingle : styles.attributeColumn}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Observation Type</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <LHDropDown label="" childList={[]} selectedValue={observationType} setSelectedValue={setObservationType} placeholder="Select Observation Type" />
                  </View>
                  <View style={isMobile ? styles.attributeColumnSingle : styles.attributeColumn}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Section</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <LHDropDown label="" childList={[]} selectedValue={section} setSelectedValue={setSection} placeholder="Select Section" />
                  </View>
                </View>
                <View style={isMobile ? styles.attributeRowSingle : styles.attributeRow}>
                  <View style={isMobile ? styles.attributeColumnSingle : styles.attributeColumn}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Roles</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <LHDropDown label="" childList={[]} selectedValue={roles} setSelectedValue={setRoles} placeholder="Select Roles" />
                  </View>
                </View>
              </View>
            )}

            {/* Leadership Walk Attributes */}
            {modules.leadershipWalk && (
              <View style={[styles.attributesSection, styles.leadershipWalkSection, isDark && styles.leadershipWalkSectionDark]}>
                <Text style={[styles.attributesSectionTitle, isDark && { color: '#81C784' }]}>Leadership Walk Attributes</Text>
                <View style={isMobile ? styles.attributeRowSingle : styles.attributeRow}>
                  <View style={[isMobile ? styles.attributeColumnSingle : styles.attributeColumn, fieldErrors.leadershipZone && (isDark ? { borderColor: colors.status.error, borderWidth: 1, borderRadius: 6, padding: 4, backgroundColor: colors.statusBackground.error } : styles.dropdownErrorWrapper)]}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Zone</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <KfaMultiSelectDropdown
                      label={isFetchingZones ? 'Zone (loading…)' : ''}
                      options={availableZones}
                      selected={leadershipZone}
                      onChange={(val) => { setLeadershipZone(val); clearFieldError('leadershipZone'); }}
                      placeholder="Select Zone"
                    />
                  </View>
                  <View style={[isMobile ? styles.attributeColumnSingle : styles.attributeColumn, fieldErrors.leadershipFrequency && (isDark ? { borderColor: colors.status.error, borderWidth: 1, borderRadius: 6, padding: 4, backgroundColor: colors.statusBackground.error } : styles.dropdownErrorWrapper)]}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Frequency</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <KfaMultiSelectDropdown
                      label=""
                      options={FREQUENCY_OPTIONS}
                      selected={leadershipFrequency}
                      onChange={(val) => { setLeadershipFrequency(val); clearFieldError('leadershipFrequency'); }}
                      placeholder="Select Frequency"
                    />
                  </View>
                </View>
              </View>
            )}

            {/* SPARC Walk Attributes */}
            {modules.sparcWalk && (
              <View style={[styles.attributesSection, styles.sparcWalkSection, isDark && styles.sparcWalkSectionDark]}>
                <Text style={[styles.attributesSectionTitle, isDark && { color: '#FFB74D' }]}>SPARC Walk Attributes</Text>
                <View style={isMobile ? styles.attributeRowSingle : styles.attributeRow}>
                  <View style={[isMobile ? styles.attributeColumnSingle : styles.attributeColumn, fieldErrors.sparcZone && (isDark ? { borderColor: colors.status.error, borderWidth: 1, borderRadius: 6, padding: 4, backgroundColor: colors.statusBackground.error } : styles.dropdownErrorWrapper)]}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Zone</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <KfaMultiSelectDropdown
                      label={isFetchingZones ? 'Zone (loading…)' : ''}
                      options={availableZones}
                      selected={sparcZone}
                      onChange={(val) => { setSparcZone(val); clearFieldError('sparcZone'); }}
                      placeholder="Select Zone"
                    />
                  </View>
                  <View style={[isMobile ? styles.attributeColumnSingle : styles.attributeColumn, fieldErrors.sparcSection && (isDark ? { borderColor: colors.status.error, borderWidth: 1, borderRadius: 6, padding: 4, backgroundColor: colors.statusBackground.error } : styles.dropdownErrorWrapper)]}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Section</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <KfaMultiSelectDropdown
                      label=""
                      options={SPARC_SECTION_OPTIONS}
                      selected={sparcSection ? [sparcSection] : []}
                      onChange={(val) => {
                        setSparcSection(val.length > 0 ? val[0] : null);
                        clearFieldError('sparcSection');
                      }}
                      placeholder="Select Section"
                      singleSelect
                    />
                  </View>
                </View>
              </View>
            )}

            {/* NHO Attributes */}
            {modules.nho && (
              <View style={[styles.attributesSection, styles.nhoSection, isDark && styles.nhoSectionDark]}>
                <Text style={[styles.attributesSectionTitle, isDark && { color: '#FFE082' }]}>NHO Attributes</Text>
                <View style={isMobile ? styles.attributeRowSingle : styles.attributeRow}>
                  <View style={isMobile ? styles.attributeColumnSingle : styles.attributeColumn}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Touch Type</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <LHDropDown label="" childList={[]} selectedValue={nhoTouchType} setSelectedValue={setNhoTouchType} placeholder="Select Touch Type" />
                  </View>
                  <View style={isMobile ? styles.attributeColumnSingle : styles.attributeColumn}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Section</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <LHDropDown label="" childList={[]} selectedValue={nhoSection} setSelectedValue={setNhoSection} placeholder="Select Section" />
                  </View>
                </View>
              </View>
            )}

            {/* Racking Inspection Attributes */}
            {modules.rackingInspection && (
              <View style={[styles.attributesSection, styles.rackingInspectionSection, isDark && styles.rackingInspectionSectionDark]}>
                <Text style={[styles.attributesSectionTitle, isDark && { color: '#CE93D8' }]}>Racking Inspection Attributes</Text>
                <View style={isMobile ? styles.attributeRowSingle : styles.attributeRow}>
                  <View style={isMobile ? styles.attributeColumnSingle : styles.attributeColumn}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Section</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <LHDropDown label="" childList={[]} selectedValue={rackingSection} setSelectedValue={setRackingSection} placeholder="Select Section" />
                  </View>
                </View>
              </View>
            )}

            {/* Site Evaluation Attributes */}
            {modules.siteEvaluation && (
              <View style={[styles.attributesSection, styles.siteEvaluationSection, isDark && styles.siteEvaluationSectionDark]}>
                <Text style={[styles.attributesSectionTitle, isDark && { color: '#FFAB91' }]}>Site Evaluation Attributes</Text>
                <View style={isMobile ? styles.attributeRowSingle : styles.attributeRow}>
                  <View style={isMobile ? styles.attributeColumnSingle : styles.attributeColumn}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Section</Text>
                      <Text style={[styles.requiredLabel, isDark && { color: colors.text.secondary }]}>*Required</Text>
                    </View>
                    <LHDropDown label="" childList={[]} selectedValue={siteEvaluationSection} setSelectedValue={setSiteEvaluationSection} placeholder="Select Section" />
                  </View>
                </View>
              </View>
            )}
          </ScrollView>

          <View style={[styles.overlayFooter, isDark && { borderTopColor: colors.border.divider, backgroundColor: colors.background.elevated }]}>
            <View style={isMobile ? styles.footerButtonsMobile : styles.footerButtons}>
              <LhButton
                text="Cancel"
                onPress={handleCancel}
                variant="secondary"
                disabled={isSaving}
                style={isMobile ? styles.footerButtonFlex : undefined}
              />
              <LhButton
                text="Save"
                onPress={handleSave}
                variant="primary"
                isLoading={isSaving}
                disabled={isSaving}
                style={isMobile ? styles.footerButtonFlex : undefined}
              />
            </View>
          </View>
        </View>
      </LHOverlay>
    </>
  );
};

export default KfaAddOverlay;
